<template>
  <!-- Chat clinica (pattern .chat-layout di _pages.scss): canali a sinistra,
       conversazione a destra, scrittura in fondo. Il contenitore dei
       messaggi (ref messagesContainer) resta quello che scorre. -->
  <div class="patient-chat text-left">
    <div v-if="!hideHeader" class="section-head mb-3">
      <b-icon icon="chat-left-dots-fill" class="section-head__icon" aria-hidden="true"></b-icon>
      <div class="list-row__body">
        <h2 class="section-title mb-0">
          {{ isCitizen ? $t("PatientChatSection.titolo_cittadino") : $t("PatientChatSection.titolo_operatore") }}
        </h2>
        <p class="list-row__meta mb-0">
          {{ isCitizen ? $t("PatientChatSection.sottotitolo_cittadino") : $t("PatientChatSection.sottotitolo_operatore") }}
        </p>
      </div>
      <b-button
        v-if="puoAbilitare"
        variant="outline-secondary"
        size="sm"
        class="ml-auto"
        @click="$refs.modalNuovoCanale.handleShow(); $bvModal.show('modal-nuovo-canale-chat')"
      >
        <b-icon icon="plus-lg" class="mr-1" aria-hidden="true"></b-icon>
        {{ $t("PatientChatSection.abilita_medico") }}
      </b-button>
    </div>

    <!-- Caricamento -->
    <div v-if="loadingChannels" class="empty-state">
      <b-spinner :label="$t('PatientChatSection.caricamento')"></b-spinner>
      <p>{{ $t("PatientChatSection.caricamento") }}</p>
    </div>

    <!-- Nessun canale -->
    <div v-else-if="channels.length === 0" class="empty-state">
      <b-icon icon="chat-square" class="empty-state__icon" aria-hidden="true"></b-icon>
      <p>
        <strong>{{ $t("PatientChatSection.nessun_canale") }}</strong><br />
        <!-- Dal menu dell'operatore non c'e' un paziente scelto: la frase non
             puo' dire "con questo paziente" -->
        <template v-if="!isCitizen && !patientHashid">{{ $t("PatientChatSection.nessun_canale_operatore_proprie") }}</template>
        <template v-else-if="!isCitizen">{{ $t("PatientChatSection.nessun_canale_operatore") }}</template>
      </p>
      <!-- Al cittadino senza chat il numero verde per richiederla -->
      <richiedi-servizio-chat v-if="isCitizen" />
      <b-button
        v-if="puoAbilitare"
        variant="primary"
        size="sm"
        @click="$refs.modalNuovoCanale.handleShow(); $bvModal.show('modal-nuovo-canale-chat')"
      >
        {{ $t("PatientChatSection.abilita_ora") }}
      </b-button>
    </div>

    <!-- Canali e conversazione. Il cittadino con solo conversazioni chiuse le
         legge, e sopra trova il numero per richiedere di nuovo il servizio -->
    <div
      v-if="isCitizen && !loadingChannels && channels.length > 0 && !servizioAttivo"
      class="tint-box tint-box--sm mb-3"
    >
      <richiedi-servizio-chat />
    </div>
    <div v-if="!loadingChannels && channels.length > 0" class="chat-layout" :class="{ 'chat-layout--single': hideChannelList }">
      <aside v-if="!hideChannelList" class="chat-channels">
        <p class="section-eyebrow mb-2">
          {{ isCitizen ? $t("PatientChatSection.elenco_cittadino") : $t("PatientChatSection.elenco_operatore") }} ({{ channels.length }})
        </p>
        <div class="chat-channels__list">
          <button
            v-for="ch in channels"
            :key="ch.hashid"
            type="button"
            class="list-row chat-channel"
            :class="{ 'chat-channel--active': selectedChannel && selectedChannel.hashid === ch.hashid }"
            :aria-pressed="selectedChannel && selectedChannel.hashid === ch.hashid ? 'true' : 'false'"
            @click="selectChannel(ch)"
          >
            <span class="list-row__icon" aria-hidden="true">
              <b-icon :icon="isCitizen ? 'person-badge-fill' : 'person-fill'"></b-icon>
            </span>
            <span class="list-row__body">
              <span class="list-row__title d-block text-truncate" :title="getChannelTitle(ch)">{{ getChannelTitle(ch) }}</span>
              <span class="list-row__meta d-block text-truncate">
                {{ getChannelSubtitle(ch) }}<template v-if="!ch.enabled"> · {{ $t("PatientChatSection.revocato") }}</template>
              </span>
            </span>
            <b-badge v-if="ch.unread_count > 0" variant="danger" pill>{{ ch.unread_count }}</b-badge>
          </button>
        </div>
      </aside>

      <section class="chat-thread-wrap">
        <template v-if="selectedChannel">
          <div class="section-head mb-2">
            <span class="list-row__icon" aria-hidden="true"><b-icon icon="person-fill"></b-icon></span>
            <div class="list-row__body">
              <p class="list-row__title mb-0">{{ getChannelTitle(selectedChannel) }}</p>
              <p class="list-row__meta mb-0">{{ getChannelSubtitle(selectedChannel) }}</p>
            </div>
            <b-button
              v-if="puoArchiviare"
              variant="outline-secondary"
              size="sm"
              class="ml-auto"
              v-b-tooltip.hover
              :title="$t('PatientChatSection.archivia_tooltip')"
              :aria-label="$t('PatientChatSection.archivia_tooltip')"
              @click="archiveCurrentChannel"
              :disabled="archiving"
            >
              <b-spinner small v-if="archiving" class="mr-1" :label="$t('PatientChatSection.archivia')"></b-spinner>
              <b-icon icon="archive" v-else class="mr-1" aria-hidden="true"></b-icon>
              {{ $t("PatientChatSection.archivia") }}
            </b-button>
          </div>

          <!-- Chi c'è: i medici della conversazione (il titolare, poi gli
               aggiunti) e, al titolare e a chi gestisce le chat, «Aggiungi».
               Solo se i medici sono più d'uno o se ne può aggiungere uno. -->
          <div v-if="mostraPartecipanti" class="tint-box tint-box--sm d-flex align-items-center flex-wrap mb-2">
            <b-icon icon="people" class="mr-2" aria-hidden="true"></b-icon>
            <span class="list-row__meta mr-auto">
              <span class="sr-only">{{ $t("ChatPartecipanti.in_chat") }}: </span>{{ nomiMedici }}
            </span>
            <b-button
              v-if="puoAggiungere"
              variant="outline-secondary"
              size="sm"
              @click="$bvModal.show('modal-aggiungi-partecipante')"
            >
              <b-icon icon="person-plus" class="mr-1" aria-hidden="true"></b-icon>
              {{ $t("ChatPartecipanti.aggiungi") }}
            </b-button>
          </div>

          <div ref="messagesContainer" class="chat-thread">
            <div v-if="loadingMessages" class="text-center py-4">
              <b-spinner small :label="$t('PatientChatSection.caricamento')"></b-spinner>
            </div>
            <div v-else-if="messages.length === 0" class="empty-state">
              <b-icon icon="chat-dots" class="empty-state__icon" aria-hidden="true"></b-icon>
              <p>
                {{ $t("PatientChatSection.nessun_messaggio") }}<br />
                {{ $t("PatientChatSection.inizia_a_scrivere") }}
              </p>
            </div>
            <template v-else>
              <div
                v-for="msg in messagesReversed"
                :key="msg.hashid"
                class="chat-bubble"
                :class="{ 'chat-bubble--mine': isMyMessage(msg) }"
              >
                <p class="chat-bubble__meta">
                  <span>{{ getSenderName(msg) }}</span>
                  <span>{{ formatTime(msg.created_at) }}</span>
                </p>
                <p class="chat-bubble__text" v-if="msg.message">{{ msg.message }}</p>
                <button
                  v-if="msg.attachment"
                  type="button"
                  class="chat-bubble__file"
                  :disabled="msg.attachment._downloading"
                  @click="downloadAttachment(msg.attachment)"
                >
                  <b-spinner small v-if="msg.attachment._downloading" class="mr-1" :label="$t('PatientChatSection.allegato')"></b-spinner>
                  <b-icon icon="paperclip" v-else class="mr-1" aria-hidden="true"></b-icon>
                  <span>{{ msg.attachment.name || msg.attachment.filename || $t("PatientChatSection.allegato") }} ({{ msg.attachment.ext || $t("PatientChatSection.file") }})</span>
                </button>
              </div>
            </template>
          </div>

          <!-- Allegato scelto -->
          <div v-if="selectedFile" class="list-row mt-2 mb-0">
            <span class="list-row__icon" aria-hidden="true"><b-icon icon="paperclip"></b-icon></span>
            <div class="list-row__body">
              <p class="list-row__title text-truncate mb-0">{{ selectedFile.name }}</p>
              <p class="list-row__meta mb-0">
                {{ $t("PatientChatSection.dimensione_file", { kb: (selectedFile.size / 1024).toFixed(1) }) }}
              </p>
            </div>
            <b-button
              variant="white"
              class="btn-icon btn-sm"
              :aria-label="$t('PatientChatSection.rimuovi_allegato')"
              @click="removeSelectedFile"
            >
              <b-icon icon="x-lg" aria-hidden="true"></b-icon>
            </b-button>
          </div>

          <!-- Scrittura -->
          <div class="chat-compose">
            <input type="file" ref="fileInput" class="d-none" @change="onFileSelected" />
            <b-button
              variant="white"
              class="btn-icon"
              :disabled="!selectedChannel.enabled || sending"
              @click="triggerFileInput"
              v-b-tooltip.hover
              :title="$t('PatientChatSection.allega_tooltip')"
              :aria-label="$t('PatientChatSection.allega_tooltip')"
            >
              <b-icon icon="paperclip" aria-hidden="true"></b-icon>
            </b-button>
            <b-form-input
              v-model="newMessage"
              :placeholder="isCitizen ? $t('PatientChatSection.placeholder_cittadino') : $t('PatientChatSection.placeholder_operatore')"
              :disabled="!selectedChannel.enabled || sending"
              :aria-label="$t('PatientChatSection.invia')"
              @keyup.enter="sendMsg"
            ></b-form-input>
            <b-button
              variant="primary"
              :disabled="(!newMessage.trim() && !selectedFile) || !selectedChannel.enabled || sending"
              @click="sendMsg"
            >
              <b-spinner small v-if="sending" :label="$t('PatientChatSection.invia')"></b-spinner>
              <template v-else>
                <b-icon icon="cursor-fill" class="mr-1" aria-hidden="true"></b-icon>
                {{ $t("PatientChatSection.invia") }}
              </template>
            </b-button>
          </div>
          <p class="chat-compose__hint">
            {{ $t("PatientChatSection.allegati_consentiti") }}
            <span v-if="!selectedChannel.enabled" class="text-danger ml-2">
              {{ $t("PatientChatSection.canale_revocato") }}
            </span>
          </p>
        </template>
        <div v-else class="empty-state">
          <b-icon icon="chat-left" class="empty-state__icon" aria-hidden="true"></b-icon>
          <p>{{ $t("PatientChatSection.seleziona_canale") }}</p>
        </div>
      </section>
    </div>

    <!-- Un altro medico nella conversazione aperta -->
    <modal-aggiungi-partecipante
      v-if="puoAggiungere"
      modal-id="modal-aggiungi-partecipante"
      :channel-hashid="selectedChannel.hashid"
      @aggiunto="alPartecipanteAggiunto"
    />

    <!-- Modale nuovo canale, già impostata sul paziente -->
    <modal-nuovo-canale
      v-if="!isCitizen"
      ref="modalNuovoCanale"
      :preset-patient-hashid="patientHashid"
      :preset-patient-name="patientFullName"
      @channel-created="loadChannels"
    />
  </div>
</template>

<script>
import { chatService, reservationService } from "@/_services";
import ModalNuovoCanale from "../../chatPazienti/ModalNuovoCanale.vue";
import ModalAggiungiPartecipante from "../../chatPazienti/ModalAggiungiPartecipante.vue";
import RichiediServizioChat from "../../chatPazienti/RichiediServizioChat.vue";

// La conversazione aperta non si aggiornava mai: i messaggi si caricavano alla
// scelta del canale e dopo un invio, e basta. Chi guardava lo schermo non vedeva
// arrivare niente, mentre il contatore in navbar - che invece interroga il server
// ogni quindici secondi - saliva.
//
// La sonda chiede il solo numero di non letti (`unread=1`): il backend risponde
// con il conteggio ed esce prima di marcare i messaggi come letti, quindi non ha
// effetti collaterali e costa una singola query. I messaggi veri si chiedono solo
// quando il conteggio dice che ce n'e' almeno uno.
//
// Il ritmo rallenta da solo quando non succede niente, cosi' una conversazione
// lasciata aperta smette di pesare.
const ATTESA_BASE = 4000;
const ATTESA_MEDIA = 10000;
const ATTESA_LUNGA = 30000;
const SONDE_PRIMA_DI_RALLENTARE = 15;
const SONDE_PRIMA_DI_FERMARSI_QUASI = 45;

export default {
  name: "PatientChatSection",
  components: {
    ModalNuovoCanale,
    ModalAggiungiPartecipante,
    RichiediServizioChat,
  },
  props: {
    patientHashid: {
      type: String,
      default: "",
    },
    patientFullName: {
      type: String,
      default: "",
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hideChannelList: {
      type: Boolean,
      default: false,
    },
    initialChannelHashid: {
      type: String,
      default: "",
    },
    // Solo il back-office della struttura legge i canali altrui: serve il permesso
    // operativo, che il singolo medico non ha
    companyScope: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      channels: [],
      selectedChannel: null,
      messages: [],
      newMessage: "",
      selectedFile: null,
      loadingChannels: false,
      loadingMessages: false,
      sending: false,
      archiving: false,
      sondaTimer: null,
      sondaAttesa: ATTESA_BASE,
      sondeVuote: 0,
    };
  },
  watch: {
    patientHashid(newVal) {
      if (newVal) {
        this.loadChannels(this.initialChannelHashid);
      }
    },
    initialChannelHashid(newVal) {
      if (newVal && this.channels.length > 0) {
        const found = this.channels.find((c) => c.hashid === newVal);
        if (found) {
          this.selectChannel(found);
        }
      }
    },
  },
  computed: {
    currentUser() {
      return (this.$store?.state?.utente && this.$store.state.utente.user) || {};
    },
    isCitizen() {
      return Number(this.currentUser?.type) === 0;
    },
    permessi() {
      return Array.isArray(this.currentUser?.permessi) ? this.currentUser.permessi : [];
    },
    // Una chat si abilita dalla scheda del paziente (il paziente è già scelto)
    // o, dal proprio elenco, se si gestiscono le chat della struttura: solo in
    // quel caso si possono cercare i pazienti
    puoAbilitare() {
      return !this.isCitizen && (!!this.patientHashid || this.permessi.includes("gestire chat pazienti"));
    },
    // Per il cittadino il servizio è attivo se ha almeno una chat aperta
    servizioAttivo() {
      return this.channels.some((ch) => ch.enabled !== false);
    },
    // I medici aggiunti al canale aperto oltre al titolare (dal 2026-09-18)
    partecipanti() {
      return Array.isArray(this.selectedChannel?.participants) ? this.selectedChannel.participants : [];
    },
    // Chi è chi legge nel canale aperto: lo manda il server (my_role) negli
    // elenchi dei propri canali; in quello della struttura si ricava
    mioRuolo() {
      const ch = this.selectedChannel;
      if (!ch) return null;
      if (ch.my_role !== undefined) return ch.my_role;
      const io = this.currentUser?.hashid;
      if (ch.operator?.hashid === io) return "titolare";
      if (this.partecipanti.some((p) => p.hashid === io)) return "partecipante";
      return this.isCitizen ? "paziente" : null;
    },
    // Aggiunge il titolare o chi gestisce le chat, a canale aperto (il server
    // fa lo stesso controllo)
    puoAggiungere() {
      if (this.isCitizen || !this.selectedChannel || !this.selectedChannel.enabled) return false;
      return this.mioRuolo === "titolare" || this.permessi.includes("gestire chat pazienti");
    },
    // Archiviare svuota la chat per tutti: non lo fa il paziente né un medico
    // aggiunto (il server rifiuta)
    puoArchiviare() {
      if (this.isCitizen) return false;
      if (this.mioRuolo === "partecipante") return this.permessi.includes("gestire chat pazienti");
      return true;
    },
    mostraPartecipanti() {
      return !!this.selectedChannel && (this.partecipanti.length > 0 || this.puoAggiungere);
    },
    // Il titolare e poi gli aggiunti; chi legge è «tu»
    nomiMedici() {
      if (!this.selectedChannel) return "";
      const io = this.currentUser?.hashid;
      return [this.selectedChannel.operator, ...this.partecipanti]
        .filter(Boolean)
        .map((m) => {
          if (m.hashid === io) return this.$t("ChatPartecipanti.tu");
          const nome = m.anagrafica?.name || m.name || "";
          const cognome = m.anagrafica?.surname || m.surname || "";
          return `${nome} ${cognome}`.trim() || m.email || "";
        })
        .join(", ")
        .replace(/^./, (c) => c.toUpperCase());
    },
    messagesReversed() {
      // Backend ritorna i messaggi ordinati per created_at desc; per la visualizzazione chat invertiamo in ordine cronologico asc
      return [...this.messages].reverse();
    },
  },
  mounted() {
    this.loadChannels(this.initialChannelHashid);
    this.avviaSonda();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.alCambioVisibilita);
    }
  },
  beforeDestroy() {
    this.fermaSonda();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.alCambioVisibilita);
    }
  },
  methods: {
    avviaSonda() {
      this.fermaSonda();
      // setTimeout concatenato invece di setInterval: se una risposta tarda, il
      // giro successivo parte dopo, e le richieste non si accavallano mai
      this.sondaTimer = setTimeout(this.eseguiSonda, this.sondaAttesa);
    },
    fermaSonda() {
      if (this.sondaTimer) {
        clearTimeout(this.sondaTimer);
        this.sondaTimer = null;
      }
    },
    riprendiRitmo() {
      this.sondeVuote = 0;
      this.sondaAttesa = ATTESA_BASE;
    },
    eseguiSonda() {
      // Scheda in secondo piano, nessun canale aperto, o una richiesta gia' in
      // corso: si salta il giro senza chiedere niente al server
      const inSecondoPiano = typeof document !== "undefined" && document.hidden;
      if (inSecondoPiano || !this.selectedChannel || this.loadingMessages || this.sending) {
        this.avviaSonda();
        return;
      }
      chatService
        .getMessages(this.selectedChannel.hashid, { unread: 1 })
        .then((res) => {
          const nonLetti = Number(res.data?.data?.unread_count || 0);
          if (nonLetti > 0) {
            this.riprendiRitmo();
            this.loadMessages(true);
          } else {
            this.sondeVuote += 1;
            if (this.sondeVuote >= SONDE_PRIMA_DI_FERMARSI_QUASI) {
              this.sondaAttesa = ATTESA_LUNGA;
            } else if (this.sondeVuote >= SONDE_PRIMA_DI_RALLENTARE) {
              this.sondaAttesa = ATTESA_MEDIA;
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.avviaSonda();
        });
    },
    alCambioVisibilita() {
      if (typeof document !== "undefined" && !document.hidden) {
        // Tornando sulla scheda si guarda subito, senza aspettare il giro
        this.riprendiRitmo();
        this.fermaSonda();
        this.eseguiSonda();
      }
    },
    loadChannels(targetChannelHashid = null) {
      this.loadingChannels = true;
      const fetchPromise = this.companyScope
        ? chatService.getCompanyChannels({ patient_user_id: this.patientHashid })
        : chatService.getMyChannels();

      fetchPromise
        .then((res) => {
          const tutti = res.data?.data?.data || res.data?.data || [];
          // Fuori dal back-office l'operatore parte dai propri canali: si tengono
          // quelli del paziente aperto
          this.channels =
            !this.companyScope && !this.isCitizen && this.patientHashid
              ? tutti.filter((c) => c.patient && c.patient.hashid === this.patientHashid)
              : tutti;
          const desiredHashid = targetChannelHashid || this.initialChannelHashid;
          const found = desiredHashid ? this.channels.find((c) => c.hashid === desiredHashid) : null;
          if (found) {
            this.selectChannel(found);
          } else if (this.channels.length > 0) {
            this.selectChannel(this.channels[0]);
          } else {
            this.selectedChannel = null;
            this.messages = [];
          }
        })
        .finally(() => {
          this.loadingChannels = false;
        });
    },
    // Leggere una conversazione cambia il badge in navbar, ma questa sezione si
    // apre da quattro posti diversi - navbar, scheda paziente, back-office,
    // profilo sanitario - e solo uno di quelli e' la navbar. Finche' il
    // conteggio si aggiornava a furia di `messages-read`, chi leggeva dalla
    // scheda paziente si teneva il numero vecchio fino al giro dopo. Lo store
    // lo sanno tutti, quindi si avvisa quello e l'evento resta per chi lo usa
    // gia' (l'elenco del back-office).
    segnalaLettura() {
      this.$emit("messages-read");
      if (this.$store) {
        this.$store.dispatch("chat/aggiorna");
      }
    },
    selectChannel(channel) {
      this.selectedChannel = channel;
      if (channel && channel.unread_count > 0) {
        channel.unread_count = 0;
        this.segnalaLettura();
      }
      this.riprendiRitmo();
      this.loadMessages();
    },
    // `silenzioso` serve alla sonda: senza, ogni messaggio in arrivo farebbe
    // sparire la conversazione dietro lo spinner per il tempo della chiamata
    loadMessages(silenzioso = false) {
      if (!this.selectedChannel) return;
      if (!silenzioso) {
        this.loadingMessages = true;
      }
      chatService
        .getMessages(this.selectedChannel.hashid)
        .then((res) => {
          this.messages = res.data?.data?.data || res.data?.data || [];
          this.segnalaLettura();
          this.scrollToBottom();
        })
        .finally(() => {
          if (!silenzioso) {
            this.loadingMessages = false;
          }
          this.scrollToBottom();
        });
    },
    scrollToBottom(smooth = false) {
      const doScroll = () => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: smooth ? "smooth" : "auto",
          });
        }
      };
      this.$nextTick(doScroll);
      setTimeout(doScroll, 50);
      setTimeout(doScroll, 150);
      setTimeout(doScroll, 300);
    },
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    onFileSelected(event) {
      const files = event.target.files;
      if (files && files[0]) {
        const file = files[0];
        const maxBytes = 4 * 1024 * 1024; // 4 MB
        if (file.size > maxBytes) {
          this.$bvToast.toast(this.$t("PatientChatSection.file_troppo_grande"), {
            title: this.$t("PatientChatSection.file_troppo_grande_titolo"),
            variant: "warning",
            solid: true,
          });
          this.removeSelectedFile();
          return;
        }
        this.selectedFile = file;
      }
    },
    removeSelectedFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    async sendMsg() {
      if ((!this.newMessage.trim() && !this.selectedFile) || !this.selectedChannel) return;
      this.sending = true;
      const text = this.newMessage;
      const fileToSend = this.selectedFile;
      let attachmentId = null;

      try {
        if (fileToSend) {
          const formData = new FormData();
          formData.append("file", fileToSend);
          formData.append("name", fileToSend.name);
          formData.append("destination", "chat");
          formData.append("has_owner", "false");
          formData.append("encrypt", "true");

          const uploadRes = await chatService.uploadAttachment(formData);
          attachmentId = uploadRes.data?.data?.hashid || uploadRes.data?.data?.id;
        }

        await chatService.sendMessage(this.selectedChannel.hashid, {
          message: text,
          attachment_id: attachmentId,
        });

        this.newMessage = "";
        this.removeSelectedFile();
        // Si e' appena scritto: la conversazione e' viva, la sonda torna svelta
        this.riprendiRitmo();
        this.loadMessages();
      } catch (err) {
        const msg = err?.data?.message || err?.message || this.$t("PatientChatSection.invio_fallito");
        this.$bvToast.toast(msg, { variant: "danger", solid: true });
      } finally {
        this.sending = false;
      }
    },
    // Si ricaricano i canali tenendo aperto quello di prima: il riepilogo porta
    // i partecipanti aggiornati
    alPartecipanteAggiunto() {
      this.loadChannels(this.selectedChannel ? this.selectedChannel.hashid : null);
    },
    archiveCurrentChannel() {
      if (!this.selectedChannel) return;
      this.archiving = true;
      chatService
        .archiveChannelToProfile(this.selectedChannel.hashid)
        .then(() => {
          this.$bvToast.toast(this.$t("PatientChatSection.archiviazione_riuscita"), {
            title: this.$t("PatientChatSection.archiviazione_titolo"),
            variant: "success",
            solid: true,
          });
          this.$emit("archive-updated");
        })
        .catch((err) => {
          const msg = err?.data?.message || this.$t("PatientChatSection.archiviazione_fallita");
          this.$bvToast.toast(msg, {
            title: this.$t("PatientChatSection.errore"),
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.archiving = false;
        });
    },
    isMyMessage(msg) {
      if (this.currentUser?.hashid && msg.sender?.hashid) {
        return msg.sender.hashid === this.currentUser.hashid;
      }
      if (this.currentUser?.id && msg.sender_user_id) {
        return msg.sender_user_id === this.currentUser.id;
      }
      return false;
    },
    getSenderName(msg) {
      if (this.isMyMessage(msg)) return "Tu";
      const s = msg.sender;
      if (s?.anagrafica?.surname) {
        return (s.type === 1 ? "Dott. " : "") + s.anagrafica.surname + " " + (s.anagrafica.name || "");
      }
      return s?.email || "Utente";
    },
    getChannelTitle(ch) {
      if (!ch) return "";
      if (this.isCitizen) {
        const op = ch.operator;
        const surname = op?.anagrafica?.surname || op?.surname;
        const name = op?.anagrafica?.name || op?.name || "";
        return surname
          ? this.$t("PatientChatSection.titolo_medico", { nome: `${surname} ${name}`.trim() })
          : op?.email || this.$t("PatientChatSection.operatore");
      } else {
        const pt = ch.patient;
        const surname = pt?.anagrafica?.surname || pt?.surname;
        const name = pt?.anagrafica?.name || pt?.name || "";
        return surname ? `${surname} ${name}` : pt?.email || this.$t("PatientChatSection.paziente");
      }
    },
    getChannelSubtitle(ch) {
      if (!ch) return "";
      return ch.company?.nome || ch.company?.name || "";
    },
    downloadAttachment(attachment) {
      if (!attachment) return;
      const hash = attachment.hashid || attachment.id;
      const fName = attachment.name || attachment.filename || "allegato.pdf";
      this.$set(attachment, "_downloading", true);

      reservationService
        .downloadResource(hash)
        .then((response) => {
          const contentType = response.headers["content-type"] || "application/pdf";
          const blob = new Blob([response.data], { type: contentType });
          const blobUrl = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fName;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
          }, 10000);
        })
        .catch(() => {
          this.$bvToast.toast(this.$t("PatientChatSection.download_fallito"), {
            title: this.$t("PatientChatSection.download_fallito_titolo"),
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.$set(attachment, "_downloading", false);
        });
    },
    formatTime(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

